/**
 * Synapsy Widget - Animação de entrada
 * Usa a Web Animations API (element.animate) para NÃO injetar CSS global na
 * página hospedeira — coerente com o restante do widget (estilos inline).
 */

const ENTRANCE_SHOWN_KEY = "synapsy_widget_entrance_shown";

// "Deslizar de baixo" (ms / px)
const LAUNCHER_DURATION = 420;
const BUBBLE_DURATION = 460;
const BUBBLE_DELAY = 220; // balão entra logo após o launcher (escalonado)
const SLIDE_OFFSET = 16; // deslocamento inicial pra baixo
const EASING = "cubic-bezier(0.22, 1, 0.36, 1)"; // ease-out suave

/**
 * Usuário pediu para reduzir movimento (acessibilidade)?
 * @returns {boolean}
 */
export function prefersReducedMotion() {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

function entranceAlreadyShown() {
  try {
    return window.sessionStorage.getItem(ENTRANCE_SHOWN_KEY) === "true";
  } catch {
    return false;
  }
}

function markEntranceShown() {
  try {
    window.sessionStorage.setItem(ENTRANCE_SHOWN_KEY, "true");
  } catch {
    // sessionStorage indisponível — sem persistência, mas não quebra
  }
}

/**
 * Tipo de navegação atual: "reload" | "navigate" | "back_forward" | "prerender".
 * Usa a Navigation Timing API (com fallback para a API legada).
 * @returns {string}
 */
function getNavigationType() {
  try {
    const nav = performance.getEntriesByType("navigation")[0];
    if (nav && nav.type) return nav.type;
  } catch {
    // ignora — tenta o fallback abaixo
  }
  try {
    // API legada (deprecated): 0 = navigate, 1 = reload, 2 = back_forward
    const legacy = performance.navigation && performance.navigation.type;
    if (legacy === 1) return "reload";
    if (legacy === 2) return "back_forward";
  } catch {
    // ignora
  }
  return "navigate";
}

/**
 * Anima um elemento subindo de baixo com fade.
 * fill: "backwards" aplica o estado inicial (invisível/deslocado) durante o
 * delay e não deixa transform/opacity residual após terminar.
 * @param {HTMLElement} el
 * @param {{ duration: number, delay?: number }} opts
 */
function slideUp(el, { duration, delay = 0 }) {
  el.animate(
    [
      { opacity: 0, transform: `translateY(${SLIDE_OFFSET}px)` },
      { opacity: 1, transform: "translateY(0)" },
    ],
    { duration, delay, easing: EASING, fill: "backwards" },
  );
}

/**
 * Toca a animação de entrada.
 * - Em RELOAD da página: anima sempre.
 * - Demais casos (primeiro acesso, troca de página, voltar/avançar): anima só
 *   na primeira aparição da sessão. Assim o primeiro acesso anima, mas navegar
 *   entre páginas do site não reanima.
 * Deve ser chamada DEPOIS de showClosedUI (elementos já com display correto).
 * @param {HTMLElement} launcher
 * @param {HTMLElement} bubble
 * @param {Object} config
 */
export function maybePlayEntrance(launcher, bubble, config) {
  if (config.entranceAnimation === false) return;

  const isReload = getNavigationType() === "reload";
  if (!isReload && entranceAlreadyShown()) return;

  // Acessibilidade: sem movimento, mas marca como exibida (não tenta de novo).
  if (prefersReducedMotion()) {
    markEntranceShown();
    return;
  }

  // WAAPI ausente (browser muito antigo): degrada sem animar.
  if (typeof launcher.animate !== "function") {
    markEntranceShown();
    return;
  }

  slideUp(launcher, { duration: LAUNCHER_DURATION });

  // Anima o balão só se ele estiver realmente visível (não dispensado).
  if (bubble.style.display !== "none") {
    slideUp(bubble, { duration: BUBBLE_DURATION, delay: BUBBLE_DELAY });
  }

  markEntranceShown();
}
